import { useState } from 'react';
import { Download } from 'lucide-react';
import {
  Button,
  Card,
  DataTable,
  DateRangeFilter,
  ErrorState,
  ExportButton,
  FilterBar,
  FilterDivider,
  FormField,
  Modal,
  PanelTitle,
  ReasonField,
  Select,
  SelectableChip,
  Stat,
  StatusPill,
  TextField,
  isReasonValid,
  type DataTableColumn,
} from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { formatDate, formatDateTime, isDay, toApiRange, type DayRange } from '../../lib/format/dates';
import { formatCents, formatCount } from '../../lib/format/money';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { notify } from '../../services/notify';
import { LocalePicker } from '../locales/LocalePicker';
import { pickParam, stringParam } from '../profiles/people';
import type { BufferLedgerRow, FilingExtractRow, JurisdictionRow, RevenueOutlier, RevenueRow } from './api';
import {
  useAuditExport,
  useBufferLedger,
  useDrawDownBuffer,
  useExportBufferLedger,
  useExportFilingExtract,
  useExportRevenue,
  useFilingExtract,
  useRevenue,
} from './queries';
import { humanStatus } from './parts';

const REPORTS = ['filing', 'audit', 'buffer', 'revenue'] as const;
const REPORT_LABEL: Record<(typeof REPORTS)[number], string> = {
  filing: 'Filing extract',
  audit: 'Audit export',
  buffer: 'Buffer ledger',
  revenue: 'Revenue',
};
const MONTH_RE = /^\d{4}-\d{2}$/;

/** Last calendar month in UTC, YYYY-MM. */
function lastMonth(now: Date = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  return d.toISOString().slice(0, 7);
}

/** Last calendar month in UTC as an inclusive day range. */
function lastMonthDays(now: Date = new Date()): DayRange {
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

/**
 * Reports (§7.11): the filing extract, the audit export, the buffer ledger and
 * revenue. All windows are UTC calendar days.
 */
export function ReportsScreen() {
  const table = useTableUrlState();
  const report = pickParam(table.filters.report, REPORTS) ?? 'filing';
  return (
    <div className="flex flex-col gap-[18px]">
      <FilterBar>
        {REPORTS.map((r) => (
          <SelectableChip
            key={r}
            active={report === r}
            onClick={() => table.setParams({ report: r === 'filing' ? undefined : r })}
          >
            {REPORT_LABEL[r]}
          </SelectableChip>
        ))}
      </FilterBar>
      {report === 'filing' && <FilingExtractReport />}
      {report === 'audit' && <AuditExportReport />}
      {report === 'buffer' && <BufferLedgerReport />}
      {report === 'revenue' && <RevenueReport />}
    </div>
  );
}

function MonthField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <>
      <label className="text-[13px] text-muted" htmlFor="report-month">
        Month (UTC)
      </label>
      <TextField
        id="report-month"
        type="month"
        surface="white"
        // Wide enough for the longest month name ("September 2026") and the picker icon.
        containerClassName="w-[220px]"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </>
  );
}

// --- filing extract ----------------------------------------------------------------------------------

const EXTRACT_COLUMNS: DataTableColumn<FilingExtractRow>[] = [
  {
    id: 'where',
    header: 'Destination',
    width: '1.6fr',
    cell: (r) => (
      <span className="min-w-0">
        <span className="block truncate font-semibold">
          {[r.city, r.stateProvince].filter(Boolean).join(', ') || '—'}
        </span>
        <span className="block text-xs text-muted">{r.postalCode}</span>
      </span>
    ),
  },
  {
    id: 'homeRule',
    header: 'Home rule',
    width: '150px',
    cell: (r) =>
      r.isHomeRule ? (
        <StatusPill tone={r.cityRegisteredAtCollection ? 'success' : 'warning'} dot>
          {r.cityRegisteredAtCollection ? 'Registered' : 'Not registered'}
        </StatusPill>
      ) : (
        <span className="text-muted">—</span>
      ),
  },
  {
    id: 'txns',
    header: 'Txns',
    width: '80px',
    align: 'right',
    numeric: true,
    cell: (r) => formatCount(r.transactionCount),
  },
  { id: 'gross', header: 'Gross', width: '1fr', align: 'right', numeric: true, cell: (r) => formatCents(r.grossCents) },
  {
    id: 'taxable',
    header: 'Taxable',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (r) => formatCents(r.taxableBaseCents),
  },
  {
    id: 'nonTaxable',
    header: 'Non-taxable',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (r) => formatCents(r.nonTaxableCents),
  },
  { id: 'tax', header: 'Tax', width: '1fr', align: 'right', numeric: true, cell: (r) => formatCents(r.taxCents) },
  { id: 'rdf', header: 'RDF', width: '100px', align: 'right', numeric: true, cell: (r) => formatCents(r.rdfCents) },
  {
    id: 'buffer',
    header: 'Buffer',
    width: '100px',
    align: 'right',
    numeric: true,
    cell: (r) => formatCents(r.bufferCents),
  },
];

const JURISDICTION_COLUMNS: DataTableColumn<JurisdictionRow>[] = [
  {
    id: 'name',
    header: 'Jurisdiction',
    width: '1.6fr',
    cell: (j) => (
      <span className="min-w-0">
        <span className="block truncate font-semibold">{j.displayName}</span>
        <span className="block text-xs text-muted">
          {humanStatus(j.level)} · {j.stateProvince}
        </span>
      </span>
    ),
  },
  {
    id: 'rate',
    header: 'Rate',
    width: '100px',
    align: 'right',
    numeric: true,
    cell: (j) => (j.percentageDecimal ? `${j.percentageDecimal}%` : '—'),
  },
  {
    id: 'txns',
    header: 'Txns',
    width: '80px',
    align: 'right',
    numeric: true,
    cell: (j) => formatCount(j.transactionCount),
  },
  {
    id: 'taxable',
    header: 'Taxable',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (j) => formatCents(j.taxableCents),
  },
  { id: 'tax', header: 'Tax', width: '1fr', align: 'right', numeric: true, cell: (j) => formatCents(j.taxCents) },
];

function FilingExtractReport() {
  const table = useTableUrlState();
  const period = stringParam(table.filters.period) ?? lastMonth();
  const state = stringParam(table.filters.state) ?? '';
  const params = { period, state: state || undefined };
  const q = useFilingExtract(params, MONTH_RE.test(period));
  const exp = useExportFilingExtract();
  const d = q.data;
  const t = d?.totals;
  const cov = d?.jurisdictionCoverage;

  return (
    <>
      <FilterBar>
        <MonthField value={period} onChange={(v) => table.setFilter('period', v || undefined)} />
        <FilterDivider />
        <TextField
          aria-label="State"
          surface="white"
          containerClassName="w-[110px]"
          placeholder="State"
          maxLength={2}
          value={state}
          onChange={(e) => table.setFilter('state', e.target.value.toUpperCase() || undefined)}
        />
        <div className="ml-auto">
          <ExportButton onExport={() => exp.mutateAsync(params).catch(() => undefined)} />
        </div>
      </FilterBar>
      {exp.error && <ErrorState error={exp.error} compact />}

      {t && (
        <Card surface="panel">
          <PanelTitle eyebrow={`Period ${d.period}`}>Totals</PanelTitle>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-2.5">
            <Stat label="Transactions" value={formatCount(t.transactionCount)} />
            <Stat label="Gross" value={formatCents(t.grossCents)} />
            <Stat label="Taxable" value={formatCents(t.taxableBaseCents)} />
            <Stat label="Non-taxable" value={formatCents(t.nonTaxableCents)} />
            <Stat label="Tax collected" value={formatCents(t.taxCents)} />
            <Stat label="RDF" value={formatCents(t.rdfCents)} />
            <Stat label="Buffer" value={formatCents(t.bufferCents)} />
          </div>
        </Card>
      )}

      <DataTable
        label="Filing extract"
        minWidth={1150}
        columns={EXTRACT_COLUMNS}
        rows={d?.rows ?? []}
        getRowId={(r) => `${r.period}|${r.stateProvince}|${r.city}|${r.postalCode}`}
        loading={q.isPending && q.fetchStatus !== 'idle'}
        error={q.error}
        onRetry={() => void q.refetch()}
        emptyLabel="No taxable sales in that period."
      />

      {(d?.jurisdictions?.length ?? 0) > 0 && (
        <div>
          <div className="mb-2 flex items-baseline justify-between gap-3">
            <div className="text-[11px] font-bold uppercase tracking-[.14em] text-muted">By taxing jurisdiction</div>
            {cov && !cov.complete && (
              <div className="text-xs text-muted">
                Split available for {formatCount(cov.snapshotsWithDetail)} of {formatCount(cov.snapshotsInPeriod)}{' '}
                transactions ({formatCents(cov.taxCentsAttributed)} of {formatCents(cov.taxCentsInPeriod)} tax).
              </div>
            )}
          </div>
          <DataTable
            label="Tax by jurisdiction"
            compact
            minWidth={760}
            columns={JURISDICTION_COLUMNS}
            rows={d!.jurisdictions!}
            getRowId={(j) => `${j.level}|${j.displayName}|${j.stateProvince}`}
          />
        </div>
      )}
    </>
  );
}

// --- audit export ------------------------------------------------------------------------------------

function AuditExportReport() {
  const [range, setRange] = useState<DayRange>(lastMonthDays());
  const run = useAuditExport();
  const window = toApiRange(range, 'utc');
  const valid = isDay(range.start) && isDay(range.end) && range.start! <= range.end!;
  const r = run.data;

  return (
    <Card surface="panel">
      <PanelTitle eyebrow="Every transaction, every field">Audit export</PanelTitle>
      <div className="flex flex-wrap items-center gap-3">
        <DateRangeFilter value={range} onChange={setRange} timeZoneLabel="UTC" />
        <Button
          size="sm"
          disabled={!valid}
          loading={run.isPending}
          onClick={() => run.mutate({ from: window.from!, to: window.to! })}
        >
          <Download size={15} aria-hidden="true" /> Download CSV
        </Button>
      </div>
      {run.error && <ErrorState error={run.error} compact />}
      {r &&
        (r.complete ? (
          <p className="mb-0 mt-3 text-sm text-green">
            Downloaded {formatCount(r.rowsReceived)} transactions — the file is complete.
          </p>
        ) : (
          <p className="mb-0 mt-3 text-sm text-danger">
            The download stopped early: {formatCount(r.rowsReceived)} rows received
            {r.rowsDeclared !== null ? ` of ${formatCount(r.rowsDeclared)}` : ''}. Don't use this file — download it
            again.
          </p>
        ))}
    </Card>
  );
}

// --- buffer ledger -----------------------------------------------------------------------------------

const LEDGER_COLUMNS: DataTableColumn<BufferLedgerRow>[] = [
  {
    id: 'date',
    header: 'Date',
    width: '120px',
    cell: (r) => <span className="text-muted">{formatDate(r.accruedDate, 'UTC')}</span>,
  },
  {
    id: 'where',
    header: 'Where',
    width: '1.4fr',
    cell: (r) => (
      <span className="min-w-0">
        <span className="block truncate font-semibold">{r.cityName || 'Locale-wide'}</span>
        <span className="block text-xs text-muted">{r.localeName}</span>
      </span>
    ),
  },
  {
    id: 'source',
    header: 'Entry',
    width: '1.4fr',
    wrap: true,
    cell: (r) => (
      <span className="text-[13px]">
        {r.drawdownCents ? 'Drawdown' : r.source === 'adjustment' ? 'Adjustment' : 'Sale'}
        {r.drawdownReason && <span className="block text-xs text-muted">{r.drawdownReason}</span>}
      </span>
    ),
  },
  {
    id: 'accrued',
    header: 'Accrued',
    width: '110px',
    align: 'right',
    numeric: true,
    cell: (r) => (r.accruedCents ? formatCents(r.accruedCents) : '—'),
  },
  {
    id: 'drawn',
    header: 'Drawn',
    width: '110px',
    align: 'right',
    numeric: true,
    cell: (r) => (r.drawdownCents ? formatCents(r.drawdownCents) : '—'),
  },
  {
    id: 'balance',
    header: 'Balance',
    width: '120px',
    align: 'right',
    numeric: true,
    cell: (r) => <span className="font-bold text-brown-deep">{formatCents(r.runningBalanceCents)}</span>,
  },
];

function BufferLedgerReport() {
  const { locked } = useSession();
  const [locale, setLocale] = useState<string | undefined>();
  const [range, setRange] = useState<DayRange>({});
  const [drawing, setDrawing] = useState(false);
  const window = toApiRange(range, 'utc');
  const params = { locale, from: window.from, to: window.to };
  const partial = Boolean(range.start) !== Boolean(range.end);
  const q = useBufferLedger(params, !partial);
  const exp = useExportBufferLedger();
  const t = q.data?.totals;
  const rows = q.data?.rows ?? [];

  return (
    <>
      <FilterBar>
        <LocalePicker
          value={locale ? [locale] : []}
          placeholder="All locales"
          onChange={(ids) => setLocale(ids[ids.length - 1])}
        />
        <FilterDivider />
        <DateRangeFilter value={range} onChange={setRange} timeZoneLabel="UTC" />
        <div className="ml-auto flex gap-2">
          <Button
            variant="ghost-dark"
            size="sm"
            disabled={!locale}
            lockedReason={locked('buffer.write')}
            onClick={() => setDrawing(true)}
          >
            Draw down
          </Button>
          <ExportButton onExport={() => exp.mutateAsync(params).catch(() => undefined)} />
        </div>
      </FilterBar>
      {exp.error && <ErrorState error={exp.error} compact />}

      {t && (
        <div className="grid grid-cols-3 gap-2.5">
          <Stat label="Accrued" value={formatCents(t.accruedCents)} />
          <Stat label="Drawn down" value={formatCents(t.drawdownCents)} />
          <Stat label="Balance" value={formatCents(t.balanceCents)} />
        </div>
      )}

      <DataTable
        label="Buffer ledger"
        minWidth={900}
        columns={LEDGER_COLUMNS}
        rows={rows}
        getRowId={(r) => r.id}
        loading={q.isPending && q.fetchStatus !== 'idle'}
        error={q.error}
        onRetry={() => void q.refetch()}
        emptyLabel="No buffer collected yet."
      />
      {drawing && locale && <DrawdownDialog localeId={locale} rows={rows} onClose={() => setDrawing(false)} />}
    </>
  );
}

function DrawdownDialog({
  localeId,
  rows,
  onClose,
}: {
  localeId: string;
  rows: BufferLedgerRow[];
  onClose: () => void;
}) {
  const draw = useDrawDownBuffer();
  const cities = new Map(rows.filter((r) => r.localeId === localeId && r.cityId).map((r) => [r.cityId!, r.cityName]));
  const [cityId, setCityId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const cents = Math.round(Number(amount) * 100);
  const valid = Number.isFinite(cents) && cents > 0 && isReasonValid(reason);

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title="Draw down the buffer"
      footer={
        <>
          <Button variant="ghost-dark" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!valid}
            loading={draw.isPending}
            onClick={() =>
              draw.mutate(
                { localeId, cityId: cityId || undefined, amountCents: cents, reason: reason.trim() },
                {
                  onSuccess: (r) => {
                    notify.success(`Drew down ${formatCents(cents)} — ${formatCents(r.remainingBufferCents)} left.`);
                    onClose();
                  },
                },
              )
            }
          >
            Draw down
          </Button>
        </>
      }
    >
      <div className="grid gap-3.5">
        <FormField label="City" htmlFor="dd-city" optional>
          <Select
            id="dd-city"
            surface="white"
            placeholder="Locale-wide"
            value={cityId}
            options={[...cities].map(([id, name]) => ({ value: id, label: name || id }))}
            onChange={(e) => setCityId(e.target.value)}
          />
        </FormField>
        <FormField label="Amount" htmlFor="dd-amount" required>
          <TextField
            id="dd-amount"
            surface="white"
            inputMode="decimal"
            leading="$"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </FormField>
        <ReasonField value={reason} onChange={setReason} />
      </div>
      {draw.error && <ErrorState error={draw.error} compact />}
    </Modal>
  );
}

// --- revenue -----------------------------------------------------------------------------------------

const REVENUE_COLUMNS: DataTableColumn<RevenueRow>[] = [
  {
    id: 'locale',
    header: 'Locale',
    width: '1.4fr',
    cell: (r) => <span className="font-semibold">{r.localeName}</span>,
  },
  { id: 'period', header: 'Period', width: '100px', cell: (r) => <span className="text-muted">{r.period}</span> },
  {
    id: 'txns',
    header: 'Txns',
    width: '80px',
    align: 'right',
    numeric: true,
    cell: (r) => formatCount(r.transactionCount),
  },
  { id: 'gross', header: 'Gross', width: '1fr', align: 'right', numeric: true, cell: (r) => formatCents(r.grossCents) },
  {
    id: 'revenue',
    header: 'Revenue',
    width: '1fr',
    align: 'right',
    numeric: true,
    cell: (r) => <span className="font-bold text-brown-deep">{formatCents(r.revenueCents)}</span>,
  },
  {
    id: 'take',
    header: 'Take rate',
    width: '100px',
    align: 'right',
    numeric: true,
    cell: (r) => `${r.takeRatePct.toFixed(1)}%`,
  },
  {
    id: 'range',
    header: 'Per order',
    width: '1.3fr',
    align: 'right',
    numeric: true,
    cell: (r) => (
      <span>
        {formatCents(r.avgRevenueCents)}
        <span className="block text-xs text-muted">
          {formatCents(r.minRevenueCents)} – {formatCents(r.maxRevenueCents)}
        </span>
      </span>
    ),
  },
  {
    id: 'band',
    header: 'Out of band',
    width: '110px',
    align: 'right',
    numeric: true,
    cell: (r) =>
      r.outOfBandCount ? (
        <span className="font-semibold text-danger">{formatCount(r.outOfBandCount)}</span>
      ) : (
        <span className="text-muted">0</span>
      ),
  },
];

const OUTLIER_COLUMNS: DataTableColumn<RevenueOutlier>[] = [
  {
    id: 'order',
    header: 'Order',
    width: '1.3fr',
    cell: (o) => <span className="truncate font-mono text-[12.5px]">{o.orderId}</span>,
  },
  {
    id: 'when',
    header: 'When',
    width: '160px',
    cell: (o) => <span className="text-muted">{formatDateTime(o.createdAt)}</span>,
  },
  { id: 'locale', header: 'Locale', width: '1fr', cell: (o) => o.localeName },
  {
    id: 'revenue',
    header: 'Revenue',
    width: '100px',
    align: 'right',
    numeric: true,
    cell: (o) => <span className="font-semibold text-danger">{formatCents(o.revenueCents)}</span>,
  },
  {
    id: 'band',
    header: 'Expected',
    width: '150px',
    align: 'right',
    numeric: true,
    cell: (o) => `${formatCents(o.expectedLowCents)} – ${formatCents(o.expectedHighCents)}`,
  },
];

function RevenueReport() {
  const table = useTableUrlState();
  const period = stringParam(table.filters.period) ?? lastMonth();
  const [locale, setLocale] = useState<string | undefined>();
  const [outliers, setOutliers] = useState(false);
  const params = { period, locale, outliers };
  const q = useRevenue(params, MONTH_RE.test(period));
  const exp = useExportRevenue();

  return (
    <>
      <FilterBar>
        <MonthField value={period} onChange={(v) => table.setFilter('period', v || undefined)} />
        <FilterDivider />
        <LocalePicker
          value={locale ? [locale] : []}
          placeholder="All locales"
          onChange={(ids) => setLocale(ids[ids.length - 1])}
        />
        <FilterDivider />
        <SelectableChip active={outliers} onClick={() => setOutliers(!outliers)}>
          Out-of-band orders
        </SelectableChip>
        <div className="ml-auto">
          <ExportButton onExport={() => exp.mutateAsync(params).catch(() => undefined)} />
        </div>
      </FilterBar>
      {exp.error && <ErrorState error={exp.error} compact />}
      <DataTable
        label="Revenue"
        minWidth={1000}
        columns={REVENUE_COLUMNS}
        rows={q.data?.rows ?? []}
        getRowId={(r) => `${r.period}|${r.localeId}`}
        loading={q.isPending && q.fetchStatus !== 'idle'}
        error={q.error}
        onRetry={() => void q.refetch()}
        emptyLabel="No orders in that period."
      />
      {outliers && (
        <div>
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[.14em] text-muted">Out-of-band orders</div>
          <DataTable
            label="Out-of-band orders"
            compact
            minWidth={820}
            columns={OUTLIER_COLUMNS}
            rows={q.data?.outliers ?? []}
            getRowId={(o) => o.orderId}
            emptyLabel="Every order landed in its expected band."
          />
        </div>
      )}
    </>
  );
}
